import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import './TestingReport.scss';
import Modal from '@mui/material/Modal';

import { Box, Tabs, Tab } from '@mui/material';
import AlarmChecksMatrix from './AlarmChecksMatrix';
import CheckSheet from './CheckSheet';
import Button from 'src/component/UI/Button';
import { getDeviceById } from 'src/module/device/selector';
import AtControlPanel from './AtControlPanel';
import FinalizeAndSignOff from './FinalizeAndSignOff';
import { Map, List, fromJS } from 'immutable';
import { getAbilities } from 'src/module/authentication/selector';
import { fetchCheckSheet, saveCheckSheet, updateCheckSheet } from 'src/module/checkSheet/action';
import { getCheckSheetReport, getCheckSheetTasks, getCheckSheetTasksLoading, getSaveCheckSheetReportLoading } from 'src/module/checkSheet/selector';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';

export default function TestingReport (props) {
  if (!props.open) {
    return null;
  }
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    deviceId,
    reportId
  } = props;
  const [tabValue, setTabValue] = useState("1");
  const tasks = useSelector(getCheckSheetTasks('alarm-check-sheet'));
  const report = useSelector(getCheckSheetReport(reportId));
  const device = useSelector(getDeviceById(deviceId));
  const isSaveLoading = useSelector(getSaveCheckSheetReportLoading('alarm-check-sheet'));
  const isLoadingTasks = useSelector(getCheckSheetTasksLoading);
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const abilities: any = useSelector(getAbilities);
  const isPhoneView = useMediaQuery(`(max-width: ${variables.phoneWidth})`);
  const canEdit = abilities.includes('edit-check-sheet-report');
  const readOnly = Boolean(reportId) && !canEdit;

  const tasksList: any = [];
  tasks.forEach(task => {
    tasksList.push({
      task_id: task.key,
      status: null,
      comment: ''
    });
  });
  const atControlPanelTable = [
    {
      title: t('testingReport.table.ehtCable'),
      key: 'EHT Cable',
      columns: [
        { label: t('testingReport.table.tag'), key: 'Tag' },
        { label: t('testingReport.table.voltage'), key: 'Voltage(V)' },
        { label: t('testingReport.table.current'), key: 'Current(A)' },
        { label: t('testingReport.table.ir'), key: 'IR' },
        { label: t('testingReport.table.resistance'), key: 'Resistance' }
      ]
    },
    {
      title: t('testingReport.table.rtdIfRequired'),
      key: 'RTD (If Required)',
      columns: [
        { label: t('testingReport.table.rtd1R'), key: 'RTD 1 R' },
        { label: t('testingReport.table.rtd2R'), key: 'RTD 2 R' },
        { label: t('testingReport.table.rtd3R'), key: 'RTD 3 R' },
        { label: t('testingReport.table.rtd4R'), key: 'RTD 4 R' }
      ]
    },
    {
      title: t('testingReport.table.ssr'),
      key: 'SSR',
      columns: [
        { label: t('testingReport.table.controlVoltageOn'), key: 'Control Voltage (ON)' },
        { label: t('testingReport.table.outputVoltageOn'), key: 'Output Voltage (ON)' },
        { label: t('testingReport.table.controlVoltageOff'), key: 'Control Voltage (Off)' },
        { label: t('testingReport.table.outputVoltageOff'), key: 'Output Voltage (Off)' }
      ]
    }
  ];
  const atJunctionTable = [
    {
      title: '',
      key: 'main',
      columns: [
        { label: t('testingReport.table.tag'), key: 'Tag' },
        { label: t('testingReport.table.voltage'), key: 'Voltage(V)' },
        { label: t('testingReport.table.current'), key: 'Current(A)' },
        { label: t('testingReport.table.ir'), key: 'IR' },
        { label: t('testingReport.table.capacitance'), key: 'Capacitance (F)' },
        { label: t('testingReport.table.resistanceOhm'), key: 'Resistance (Ω)' }
      ]
    },
    {
      title: t('testingReport.table.atRtd1'),
      key: 'rtd1',
      columns: [{ label: t('testingReport.table.resistanceOhm'), key: 'Resistance (Ω)' }]
    },
    {
      title: t('testingReport.table.atRtd2'),
      key: 'rtd2',
      columns: [{ label: t('testingReport.table.resistanceOhm'), key: 'Resistance (Ω)' }]
    },
    {
      title: t('testingReport.table.atRtd3'),
      key: 'rtd3',
      columns: [{ label: t('testingReport.table.resistanceOhm'), key: 'Resistance (Ω)' }]
    },
    {
      title: t('testingReport.table.atRtd4'),
      key: 'rtd4',
      columns: [{ label: t('testingReport.table.resistanceOhm'), key: 'Resistance (Ω)' }]
    }
  ];
  const atJunctionRowObject = {
  };
  atJunctionTable.map(x => x.columns.map(col => {
    const title = x.key === 'main' ? col.key : `${x.key}-${col.key}`;
    atJunctionRowObject[title] = '';
    return;
  }));

  const atControlPanelOutput = {};
  atControlPanelTable.forEach((section) => {
    atControlPanelOutput[section.key] = {};
    section.columns.forEach(col => {
      atControlPanelOutput[section.key][col.key] = '';
    });
  });
  const emptyResults = Map({
    info: {
      type: 'alarm-check-sheet',
      deviceId,
      alarms: List([])
    },
    checkSheet: fromJS(tasksList),
    extras: Map({
      atControlPanel: atControlPanelOutput,
      atJunction: fromJS([atJunctionRowObject])
    }),
    signOff: Map({
      recommendations: '',
    })
  });
  const [allResults, setAllResults] = useState(tasks.size !== 0 ? emptyResults: null);

  useEffect(() => {
    if (reportId) {
      dispatch(fetchCheckSheet(reportId, 'alarm-check-sheet'));
    } else {
      setAllResults(emptyResults);
    }
  }, [reportId]);

  useEffect(() => {
    if (reportId) {
      setAllResults(Map(report));
    }
  }, [report, reportId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const next = () => {
    const next = parseInt(tabValue) + 1;
    setTabValue(next.toString());
  };
  const previous = () => {
    const next = parseInt(tabValue) - 1;
    setTabValue(next.toString());
  };
  const submit = () => {
    if (reportId) {
      dispatch(updateCheckSheet(allResults, deviceId, 'alarm-check-sheet'));
    }
    else {
      dispatch(saveCheckSheet('alarm-check-sheet', allResults, deviceId));
    }
  };
  const change = (tab, value) => {
    setAllResults(prevAllResults => {
      const updatedResults = prevAllResults.setIn(tab, value);
      return updatedResults;
    });
  };


  const nextButton = () => {
    return <Button className='next-button' onClick={next} cta>{t('testingReport.modal.next')}</Button>;
  };

  const submitButton = () => {
    return <Button onClick={submit} disabled={readOnly} loading={isSaveLoading} cta>{reportId ? t('testingReport.modal.save') : t('testingReport.modal.submit')}</Button>;
  };
  return (
    <Modal open={props.open} onClose={props.handleClose} style={{ height: '90vh' }} >
      <div className={`modal testing-report-modal${isPhoneView ? ' modal-no-padding-sides' : ''}`}>
        {!isMobile ? <h2>{t('testingReport.modal.title')} <b>({device.get('tag')})</b></h2> : null}
        {isLoadingTasks? <div style={{ margin: 'auto', marginTop: '200px', width: '100px' }}><LogoSpinner size={100} /></div> :
          <>
            <Box sx={{ width: '100%', marginBottom: '20px' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                textColor='primary'
                indicatorColor='primary'
                aria-label="testing-report-tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="1" label={<span className='tab-label-size'>{t('testingReport.modal.alarmChecksMatrix')}</span>} />
                <Tab value="2" label={<span className='tab-label-size'>{t('testingReport.modal.checkSheet')}</span>} />
                <Tab value="3" label={<span className='tab-label-size'>{t('testingReport.modal.atDevice')}</span>} />
                <Tab value="4" label={<span className='tab-label-size'>{t('testingReport.modal.signOffComments')}</span>} />

              </Tabs>
            </Box>
            {tabValue === '1' ? <AlarmChecksMatrix readOnly={readOnly} alarms={(allResults.getIn(['info', 'alarms'], List([])) as any)?.map(x => x)} onChange={change} /> : null}
            {tabValue === '2' ? <CheckSheet readOnly={readOnly} onChange={change} data={allResults.get('checkSheet')} checkSheetType="alarm-check-sheet" /> : null}
            {tabValue === '3' ? <AtControlPanel readOnly={readOnly} data={allResults.get('extras')} onChange={change} tableInfo={atControlPanelTable} atJunctionTable={atJunctionTable} /> : null}
            {tabValue === '4' ? <FinalizeAndSignOff readOnly={readOnly} data={allResults.get('signOff')} onChange={change} /> : null}

            <div className='button-bar stay-at-bottom'>
              <Button onClick={() => props.handleClose()}>{t('testingReport.modal.cancel')}</Button>

              {tabValue !== '1' ? <Button onClick={previous} >{t('testingReport.modal.previous')}</Button> : null}
              {tabValue === '4' ?
                submitButton()
                :
                nextButton()
              }
            </div>
          </>
        }

      </div>
    </Modal>
  );
}

TestingReport.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
